import axios, { InternalAxiosRequestConfig, CreateAxiosDefaults } from 'axios'

import { getAccessToken } from '@privy-io/react-auth'

// CRITICAL: Clear ANY global axios defaults that might have old HS256 tokens
// This ensures no old tokens persist across the application
if (axios.defaults?.headers?.common) {
  delete axios.defaults.headers.common['Authorization']
  delete axios.defaults.headers.common['authorization']
}
if (axios.defaults?.headers?.get) {
  delete axios.defaults.headers.get['Authorization']
  delete axios.defaults.headers.get['authorization']
}
if (axios.defaults?.headers?.post) {
  delete axios.defaults.headers.post['Authorization']
  delete axios.defaults.headers.post['authorization']
}
if (axios.defaults?.headers?.put) {
  delete axios.defaults.headers.put['Authorization']
  delete axios.defaults.headers.put['authorization']
}
if (axios.defaults?.headers?.delete) {
  delete axios.defaults.headers.delete['Authorization']
  delete axios.defaults.headers.delete['authorization']
}
if (axios.defaults?.headers?.patch) {
  delete axios.defaults.headers.patch['Authorization']
  delete axios.defaults.headers.patch['authorization']
}

const handleSuccessRequest = async (req: InternalAxiosRequestConfig) => {
  // Clear any cached authorization headers in axios defaults
  const instance = (req as any).instance || req
  if (instance?.defaults?.headers?.common) {
    delete instance.defaults.headers.common['Authorization']
    delete instance.defaults.headers.common['authorization']
  }

  // Remove old tokens from storage (silent cleanup)
  const oldToken = localStorage.getItem('token')
  if (oldToken) localStorage.removeItem('token')

  const sessionToken = sessionStorage.getItem('token')
  if (sessionToken) sessionStorage.removeItem('token')

  // Clear persisted Redux auth tokens if present
  const persistedAuth = localStorage.getItem('persist:auth')
  if (persistedAuth) {
    try {
      const authState = JSON.parse(persistedAuth)
      if (authState?.token) {
        delete authState.token
        localStorage.setItem('persist:auth', JSON.stringify(authState))
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Clear old HS256 tokens from request headers
  if (req.headers.authorization) {
    const existingAuth = req.headers.authorization as string
    if (existingAuth.startsWith('Bearer ')) {
      const token = existingAuth.split(' ')[1]
      try {
        const header = JSON.parse(atob(token.split('.')[0]))
        if (header.alg === 'HS256') {
          delete req.headers.authorization
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }
  }

  // Get Privy token
  try {
    const isEdgeBrowser = /Edg/.test(navigator.userAgent)

    // Retry logic for Edge browser
    const getPrivyTokenWithRetry = async (maxAttempts = 3): Promise<string | null> => {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          if (isEdgeBrowser && attempt > 1) {
            await new Promise(resolve => setTimeout(resolve, 100 * attempt))
          }
          const token = await getAccessToken()
          if (token) return token
        } catch (error) {
          if (attempt === maxAttempts) throw error
        }
      }
      return null
    }

    const privyToken = isEdgeBrowser
      ? await getPrivyTokenWithRetry(3)
      : await getAccessToken()

    if (privyToken) {
      // Validate token format and algorithm
      const tokenParts = privyToken.split('.')
      if (tokenParts.length !== 3) {
        delete req.headers.authorization
        return req
      }

      try {
        const header = JSON.parse(atob(tokenParts[0]))
        const payload = JSON.parse(atob(tokenParts[1]))

        // Verify token user matches Redux user
        const reduxState = localStorage.getItem('persist:auth')
        if (reduxState) {
          try {
            const authState = JSON.parse(reduxState)
            const userString = authState.user
            if (userString && userString !== 'null') {
              const reduxUser = JSON.parse(userString)
              if (reduxUser?.privy_did && reduxUser.privy_did !== payload.sub) {
                // User mismatch - reject token
                delete req.headers.authorization
                return req
              }
            }
          } catch (e) {
            // Ignore validation errors
          }
        }

        // Reject old HS256 tokens
        if (header.alg === 'HS256') {
          delete req.headers.authorization
          return req
        }

        // Verify it's a valid Privy algorithm
        const validPrivyAlgorithms = ['EdDSA', 'RS256', 'ES256']
        if (!validPrivyAlgorithms.includes(header.alg)) {
          delete req.headers.authorization
          return req
        }
      } catch (e) {
        delete req.headers.authorization
        return req
      }

      // Set authorization header
      req.headers['cache-control'] = 'no-cache, no-store, must-revalidate'
      req.headers['authorization'] = `Bearer ${privyToken}`
    } else {
      delete req.headers.authorization
    }
  } catch (error: any) {
    delete req.headers.authorization
  }

  return req
}

const handleError = (error: any) => {
  return Promise.reject(error)
}

const handleResponseError = async (error: any) => {
  // Handle 401 Unauthorized errors
  if (error?.response?.status === 401) {
    console.error('⚠️ 401 Unauthorized error received')
    console.error('Request URL:', error?.config?.url)
    console.error('Request headers:', error?.config?.headers)

    // SIMPLIFIED LOGIC: Just reject the 401 error and let the UI handle it
    // The session mismatch detection in App.tsx will:
    // 1. Detect when Privy session is lost but Redux still has user
    // 2. Clear Redux state and show login prompt
    // 3. Preserve tournament URL for redirect after re-login

    console.warn('⚠️ Authentication failed - rejecting request')
    console.warn('⚠️ Session mismatch detection in App.tsx will handle clearing state and showing login prompt')

    // Just reject - don't redirect, don't clear storage
    // Let the App.tsx session detection handle everything
    return Promise.reject(error)
  }

  return Promise.reject(error)
}

export const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://cyberlete.herokuapp.com/api/v1'

export const getAxiosInstance = () => {
  const isEdgeBrowser = /Edg/.test(navigator.userAgent)

  const config: CreateAxiosDefaults = {
    baseURL: BASE_URL,
    responseType: 'json',
    timeout: 30000, // 30 second timeout for all requests
    headers: {
      'content-type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
      ...(isEdgeBrowser && {
        'X-Edge-Browser': 'true',
        'X-Requested-With': 'XMLHttpRequest',
      }),
    },
  }

  const instance = axios.create(config)

  // Clear any default authorization headers
  delete instance.defaults.headers.common['Authorization']
  delete instance.defaults.headers.common['authorization']
  delete instance.defaults.headers.get['Authorization']
  delete instance.defaults.headers.get['authorization']
  delete instance.defaults.headers.post['Authorization']
  delete instance.defaults.headers.post['authorization']
  delete instance.defaults.headers.put['Authorization']
  delete instance.defaults.headers.put['authorization']
  delete instance.defaults.headers.delete['Authorization']
  delete instance.defaults.headers.delete['authorization']
  delete instance.defaults.headers.patch['Authorization']
  delete instance.defaults.headers.patch['authorization']

  instance.interceptors.request.use(handleSuccessRequest, handleError)
  instance.interceptors.response.use(
    (response) => response,
    handleResponseError
  )

  return instance
}
